import { Link } from "react-router";
import {
  Clock3,
  ExternalLink,
  Flag,
  ImageOff,
  Loader2,
  RotateCcw,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import type { PublicEchoFeedbackRecord } from "@/server/echoFeedback";
import {
  formatDateTime,
  formatVideoTimestamp,
} from "../echoflicks/sessionUtils";
import { FeedbackCategoryChips } from "./FeedbackCategoryChips";
import {
  FeedbackMetadataStatusBadge,
  FeedbackNotificationStatusBadge,
} from "./FeedbackStatusBadges";
import { buildFeedbackEchoHref } from "./feedbackDeepLink";
import { feedbackReporterLabel } from "./feedbackRecordView";

export const FeedbackTable = ({
  records,
  onSelect,
  onRetry,
  retryingId,
}: {
  records: PublicEchoFeedbackRecord[];
  onSelect: (record: PublicEchoFeedbackRecord) => void;
  onRetry: (record: PublicEchoFeedbackRecord) => void;
  retryingId: string | null;
}) => (
  <div className="overflow-x-auto">
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[90px]">Details</TableHead>
          <TableHead className="min-w-[210px]">Categories</TableHead>
          <TableHead className="min-w-[280px]">Feedback</TableHead>
          <TableHead className="w-[200px]">Echo</TableHead>
          <TableHead className="w-[110px]">Timestamp</TableHead>
          <TableHead className="hidden w-[150px] md:table-cell">
            Reported by
          </TableHead>
          <TableHead className="hidden w-[180px] lg:table-cell">
            Submitted
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {records.map((record) => (
          <TableRow key={record.id}>
            <TableCell className="align-top">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onSelect(record)}
                aria-label={`Open feedback details for ${record.echo.name}`}
              >
                {record.hasFrame ? (
                  <Flag className="size-3.5" />
                ) : (
                  <ImageOff className="size-3.5" />
                )}
                View
              </Button>
            </TableCell>
            <TableCell className="align-top">
              <FeedbackCategoryChips record={record} />
              <div className="mt-2 flex flex-wrap gap-1">
                <FeedbackMetadataStatusBadge status={record.metadata.status} />
                <FeedbackNotificationStatusBadge
                  status={record.notification.status}
                />
                {record.notification.status !== "sent" ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    disabled={retryingId === record.id}
                    onClick={() => onRetry(record)}
                  >
                    {retryingId === record.id ? (
                      <Loader2 className="size-3 animate-spin" />
                    ) : (
                      <RotateCcw className="size-3" />
                    )}
                    Retry Slack
                  </Button>
                ) : null}
              </div>
            </TableCell>
            <TableCell className="max-w-md whitespace-normal align-top">
              <button
                type="button"
                className="text-left text-sm hover:underline"
                onClick={() => onSelect(record)}
              >
                {record.comment}
              </button>
              <p className="mt-1 text-xs text-muted-foreground md:hidden">
                {feedbackReporterLabel(record)} ·{" "}
                {formatDateTime(record.submittedAt)}
              </p>
            </TableCell>
            <TableCell className="align-top">
              <Link
                to={buildFeedbackEchoHref(record)}
                className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
              >
                <span className="max-w-[170px] truncate">
                  {record.echo.name}
                </span>
                <ExternalLink className="size-3 shrink-0" />
              </Link>
            </TableCell>
            <TableCell className="align-top">
              <Badge variant="outline" className="font-mono">
                <Clock3 className="size-3" />
                {formatVideoTimestamp(record.position.timestampMs / 1000)}
              </Badge>
            </TableCell>
            <TableCell className="hidden align-top text-sm md:table-cell">
              {feedbackReporterLabel(record)}
            </TableCell>
            <TableCell className="hidden align-top text-sm text-muted-foreground lg:table-cell">
              {formatDateTime(record.submittedAt)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);
